import { ref, reactive, computed } from 'vue'
import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import { FetchError } from 'ofetch'
import { useGiftRepository } from '@/composables/api/repositories/gift'
import { useOtpRepository } from '@/composables/api/repositories/otp'
import { useClientStoreRepository } from '@/composables/api/repositories/client-store'
import type { CampaignGiftGetResponseBody } from '@/types/redemption'
import { useIntervalFn } from '@vueuse/core'
import { piniaPluginPersistedstate, useCookie } from '#imports'
import type {
  StoreSearchParams,
  StoreSearchResponse,
} from '~/composables/api/useStore'
import { CurrencyCodeByCountry } from '@/utils/constants/country'

const GIFT_DATA_EXPIRY = 60 * 60 * 1000 // 1 hour in milliseconds
const REFRESH_INTERVAL = 60000 // 1 minute
const zunniesFilterKeywords = [
  'ZunniesSports',
  'nonRxSunglasses',
  'Optiflex',
  'PremiumEyeglasses',
]

export const useGuestStore = defineStore(
  'guest',
  () => {
    const guest = reactive<{
      email: string | undefined
      // This is for admin order scenario where the logged in admin is different than the admin gift returned by backend.
      // We want emails to go to the admin initiating the order
      adminEmail: string | undefined
      storeId: string | undefined
      userId: string | undefined
      token: string | undefined
      loggedInToken: string | undefined
      store:
        | (StoreSearchParams & {
            currentCatalog: 'vision' | 'safety'
            isCorpPerkStore: boolean
          })
        | undefined
    }>({
      email: undefined,
      adminEmail: undefined,
      storeId: undefined,
      userId: undefined,
      token: undefined,
      loggedInToken: undefined,
      store: undefined,
    })

    const requestError = ref<FetchError | null>()
    const gift = ref<CampaignGiftGetResponseBody | null>(null)
    const dependents = ref<CampaignGiftGetResponseBody[] | null>(null)
    const currentGiftId = ref<string>('')
    const lastGiftFetchTimestamp = ref<number>(0)
    const lastDependentFetchTimestamp = ref<number>(0)

    const currentGift = computed<CampaignGiftGetResponseBody | null>(() => {
      if (isGuestMode.value) {
        return gift.value
      }
      if (gift.value && gift.value.id === currentGiftId.value) {
        return gift.value
      }

      return dependents.value?.find((g) => g.id === currentGiftId.value) || null
    })

    const isAdminGift = computed<boolean>(() => {
      return gift.value?.adminPurchase ?? gift.value?.recipientId === 'admin'
    })

    const loading = computed<boolean>(() => {
      if (import.meta.server) {
        return true
      }

      if (isGuestMode.value) {
        return gift.value === null
      }

      // Employee mode: needs gift, dependents, and token
      return (
        (!isAdminGift.value && dependents.value === null) ||
        gift.value === null ||
        !(guest.token || guest.loggedInToken)
      )
    })

    const isSafetyCatalog = computed(
      () =>
        currentGift.value?.policy?.catalogs?.includes('safety') ||
        guest.store?.currentCatalog === 'safety' ||
        false,
    )

    const setCurrentGift = (targetGiftId: string) => {
      currentGiftId.value = targetGiftId
    }

    const setLoggedInToken = (token: string) => {
      guest.loggedInToken = token
    }

    const switchStoreToken = async (storeId: string) => {
      if (guest.loggedInToken) {
        requestError.value = null
        try {
          const response = await clientStoreApi.execute<'switchStore'>(
            clientStoreApi.switchStore(storeId),
          )
          if (response?.token) {
            setLoggedInToken(response.token)
          }
        } catch (error) {
          const fetchError = clientStoreApi.getError(error)
          if (fetchError) {
            requestError.value = fetchError
          }
        }
      }
    }

    const calculateGiftAmt = (g: CampaignGiftGetResponseBody): number => {
      if (g.usage?.type === 'freeItem') {
        return g.usage.budget ?? 0
      } else if (g.usage?.type === 'giftCard') {
        return g.usage.balance ?? 0
      }
      return 0
    }

    const giftAmount = computed(() =>
      gift.value ? calculateGiftAmt(gift.value) : 0,
    )

    const giftApi = useGiftRepository()
    const otpApi = useOtpRepository()
    const clientStoreApi = useClientStoreRepository()

    const getCurrencyFromGift = (g: CampaignGiftGetResponseBody): string => {
      return g.usage?.currency ?? CurrencyCodeByCountry.us.currencyCode
    }

    const setGift = (response: CampaignGiftGetResponseBody) => {
      guest.store = undefined
      gift.value = {
        ...response,
        currencyCode: getCurrencyFromGift(response),
      }
      ;({
        email: guest.email,
        storeId: guest.storeId,
        userId: guest.userId,
      } = response)

      if (!currentGiftId.value) {
        currentGiftId.value = response.id || ''
      }
      lastGiftFetchTimestamp.value = Date.now()
    }

    const loadDependents = async (email?: string) => {
      if (isAdminGift.value) {
        dependents.value = []
        return
      }
      const emailToUse = email || guest.email || ''
      try {
        const response = await giftApi.execute<'getDependentGifts'>(
          giftApi.getDependentGifts(emailToUse),
        )
        if (response) {
          dependents.value = []
          response.forEach((g) => {
            if (g.id !== gift.value?.id) {
              dependents.value?.push({
                ...g,
                currencyCode: getCurrencyFromGift(g),
              })
            }
          })
          lastDependentFetchTimestamp.value = Date.now()
        }
      } catch (error) {
        const fetchError = giftApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
        }
        dependents.value = []
      }
    }

    const loadGift = async (id: string, isAdminGift: boolean = false) => {
      const employerStoreId = useCookie('employer_store_id')
      const employerCampaignId = useCookie('employer_campaign_id')

      try {
        const response = await giftApi.execute<'getAdminGift' | 'getGiftById'>(
          isAdminGift
            ? giftApi.getAdminGift(
                employerCampaignId.value || '',
                employerStoreId.value || '',
              )
            : giftApi.getGiftById(id),
        )

        if (response) {
          guest.store = undefined
          gift.value = {
            ...response,
            currencyCode: getCurrencyFromGift(response),
          }
          ;({
            email: guest.email,
            storeId: guest.storeId,
            userId: guest.userId,
          } = response)

          if (!currentGiftId.value) {
            currentGiftId.value = response.id || ''
          }
          lastGiftFetchTimestamp.value = Date.now()
        }
      } catch (error) {
        const fetchError = giftApi.getError(error)
        if (fetchError) {
          requestError.value = fetchError
        }
      }
    }

    const recreateCatalogFilter = (catalog?: string) => {
      if (!catalog) return {}
      return {
        product: `(Redemption_Catalog:${catalog})`,
        'product-variant': `(Redemption_Catalog_search:${catalog})`,
      }
    }

    const setPartnerStoreGuest = (
      storeData: StoreSearchResponse,
      storeParams: StoreSearchParams,
    ) => {
      const currentCatalog =
        guest.store?.currentCatalog ??
        (storeData.catalog.length === 1
          ? (storeData.catalog[0] as 'vision' | 'safety')
          : 'vision')

      gift.value = {
        storeId: storeData.storeId,
        code: storeData.storeCode,
        storeName: storeData.storeName,
        catalogFilter: recreateCatalogFilter(currentCatalog),
        catalogs: storeData.catalog,
        partnerId: storeData.partnerId,
        algoliaIndex: storeData.algoliaIndex,
        frameFilter: storeData.frameFilter,
        lensFilter: storeData.lensFilter,
        discountPercentage: storeData.discountPercentage,
        orderMinimum: storeData.orderMinimum,
        promotionCode: storeData.couponCode,
        currencyCode: storeData.currency,
      }
      guest.email = undefined
      guest.storeId = storeData.storeId
      guest.userId = undefined
      guest.token = undefined
      guest.loggedInToken = undefined
      guest.store = {
        ...storeParams,
        currentCatalog,
        isCorpPerkStore: storeData.isCorpPerkStore ?? false,
      }
      currentGiftId.value = ''
      dependents.value = []
    }

    const switchSelectedCatalog = (catalog: 'vision' | 'safety') => {
      if (guest.store) {
        guest.store.currentCatalog = catalog
        gift.value = {
          ...gift.value,
          catalogFilter: recreateCatalogFilter(catalog),
        }
      }
    }

    const isGuestMode = computed(
      () => !gift.value?.id || typeof guest.store?.partnerName === 'string',
    )

    useIntervalFn(async () => {
      const shouldRefreshGift =
        Date.now() - lastGiftFetchTimestamp.value > GIFT_DATA_EXPIRY

      if (guest.token && gift.value?.id && shouldRefreshGift) {
        await loadGift(gift.value.id)
      }
    }, REFRESH_INTERVAL)

    useIntervalFn(async () => {
      const shouldRefreshDependents =
        Date.now() - lastDependentFetchTimestamp.value > GIFT_DATA_EXPIRY

      if (guest.token && guest.email && shouldRefreshDependents) {
        await loadDependents(guest.email)
      }
    }, REFRESH_INTERVAL)

    const loginGuestUser = async () => {
      try {
        const response = await otpApi.execute<'guestLogin'>(
          otpApi.guestLogin({
            userEmail: guest.email ?? '',
            userId: guest.userId ?? '',
            storeId: guest.storeId ?? '',
          }),
        )
        if (response?.token) {
          guest.token = response.token
        }
      } catch (error) {
        const fetchError = otpApi.getError(error)
        if (fetchError) {
          requestError.value = fetchError
        }
      }
    }

    const loginCorpPerkStoreGuestUser = async () => {
      try {
        const response = await otpApi.execute<'corpPerkStoreGuestLogin'>(
          otpApi.corpPerkStoreGuestLogin({
            storeId: guest.storeId ?? '',
          }),
        )
        if (response?.token) {
          guest.token = response.token
        }
      } catch (error) {
        const fetchError = otpApi.getError(error)
        if (fetchError) {
          requestError.value = fetchError
        }
      }
    }

    const primaryUser = computed<{
      firstName: string
      lastName: string
      email: string
      applicationId: string
    } | null>(() => {
      if (guest.loggedInToken) {
        const decodedToken = jwtDecode<{
          firstName: string
          lastName: string
          email: string
          applicationId: string
        }>(guest.loggedInToken)
        return decodedToken
      }
      return null
    })

    const isCorpPerkStore = computed(() => {
      return guest.store?.isCorpPerkStore ?? false
    })

    const isZunniesStore = computed(() =>
      zunniesFilterKeywords.some((keyword) =>
        gift.value?.frameFilter?.includes(keyword),
      ),
    )

    const hasMultipleGuestCatalogs = computed(
      () => (gift.value?.catalogs?.length || 0) > 1,
    )

    const reset = () => {
      gift.value = null
      currentGiftId.value = ''
      dependents.value = null
      guest.email = undefined
      guest.storeId = undefined
      guest.userId = undefined
      guest.token = undefined
      guest.loggedInToken = undefined
      requestError.value = null
      lastGiftFetchTimestamp.value = 0
      lastDependentFetchTimestamp.value = 0
      guest.store = undefined
    }

    return {
      loading,
      guest,
      primaryUser,
      gift,
      currentGiftId,
      currentGift,
      isSafetyCatalog,
      setCurrentGift,
      setGift,
      setLoggedInToken,
      switchStoreToken,
      dependents,
      giftAmount,
      requestError,
      loadGift,
      loadDependents,
      loginGuestUser,
      loginCorpPerkStoreGuestUser,
      reset,
      calculateGiftAmt,
      setPartnerStoreGuest,
      switchSelectedCatalog,
      hasMultipleGuestCatalogs,
      isGuestMode,
      isAdminGift,
      isCorpPerkStore,
      isZunniesStore,
    }
  },
  {
    persist: [
      {
        storage: piniaPluginPersistedstate.cookies({
          sameSite: 'strict',
        }),
        pick: ['guest', 'gift', 'currentGiftId'],
        omit: [
          'gift.createAt',
          'gift.updateAt',
          'gift.deleteAt',
          'gift.schedule',
          'gift.userEmail',
        ],
      },
    ],
  },
)
